/**
 * Profit and loss, and Trends.
 *
 * The design table in DESIGN.md gives Finance four routes, so Trends lives here as a second
 * section rather than at a URL that does not exist. Its content is what the brief asked
 * for: revenue and margin over time, new versus returning, and turnover by SKU, all from
 * the same metric layer with the same badges, so a figure on this page and the same figure
 * on Home cannot disagree.
 *
 * A request for a fifth Finance route is in INTEGRATION_NOTES. Until it is answered, this
 * page carries both sections and the sub-nav stays as designed.
 */
import { useState } from 'react';
import type { WindowKind } from '@elutide/shared';
import {
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  MoneyText,
  Pill,
  Skeleton,
  type Column,
} from '@/components';
import { formatMoney, formatPercent } from '@/lib/format';
import { usePnl, useProductPnl } from './api';
import { WindowChips } from './Overview';
import type { ProductPnlRow } from './types';
import './finance.css';

/** The order the profit and loss reads in. Anything not named here follows, alphabetically. */
const LINE_ORDER = [
  'revenue_net',
  'paid_revenue',
  'refunds',
  'product_revenue',
  'shipping_revenue',
  'protection_revenue',
  'cogs',
  'processor_fees',
  'fulfillment',
  'marketing',
  'operating',
  'true_net',
  'gross_margin_pct',
  'contribution_margin_pct',
  'net_margin_pct',
  'discount_rate_pct',
  'sales_tax_collected',
];

const LINE_LABELS: Record<string, string> = {
  revenue_net: 'Revenue net',
  paid_revenue: 'Paid revenue, before refunds',
  refunds: 'Refunds, by refund date',
  product_revenue: 'Product revenue (line items only)',
  shipping_revenue: 'Shipping charged',
  protection_revenue: 'SafeArrival protection',
  cogs: 'COGS at frozen unit cost',
  processor_fees: 'Processor fees',
  fulfillment: 'Shipping and packaging',
  marketing: 'Ads, partner CPA and rewards issued',
  operating: 'Fixed costs and shrink',
  true_net: 'True net',
  gross_margin_pct: 'Gross margin',
  contribution_margin_pct: 'Contribution margin',
  net_margin_pct: 'Net margin',
  discount_rate_pct: 'Discount rate',
  sales_tax_collected: 'Sales tax collected (pass through, not revenue)',
};

export function PnlPage() {
  const [window, setWindow] = useState<WindowKind>('30d');
  const pnl = usePnl(window);
  const products = useProductPnl(window, 100);

  if (pnl.isError) {
    return (
      <ErrorState
        message="The profit and loss could not be loaded."
        onRetry={() => {
          void pnl.refetch();
        }}
      />
    );
  }

  const lines = [...(pnl.data?.lines ?? [])].sort((a, b) => {
    const ai = LINE_ORDER.indexOf(a.metric_id);
    const bi = LINE_ORDER.indexOf(b.metric_id);
    if (ai === -1 && bi === -1) return a.metric_id.localeCompare(b.metric_id);
    if (ai === -1) return 1;
    if (bi === -1) return -1;
    return ai - bi;
  });

  const coverage = pnl.data?.fee_coverage;

  return (
    <div className="o-stack">
      <WindowChips value={window} onChange={setWindow} />

      <Card
        title="Profit and loss"
        basis="woo_paid"
        action={
          coverage ? (
            <Pill tone={coverage.actual_share === null ? 'neutral' : coverage.actual_share >= 0.98 ? 'ok' : 'warn'}>
              {coverage.actual_share === null
                ? 'no fees in window'
                : `fees ${formatPercent(coverage.actual_share)} actual`}
            </Pill>
          ) : null
        }
      >
        {pnl.isLoading ? (
          <Skeleton shape="row" lines={8} />
        ) : lines.length === 0 ? (
          <EmptyState message="No lines for this window." />
        ) : (
          <div className="o-ledger-scroll" data-allow-hscroll="true">
            {lines.map((line) => (
              <div key={line.metric_id} className="o-kv">
                <span className="o-kv__label">{LINE_LABELS[line.metric_id] ?? line.label.replace(/_/g, ' ')}</span>
                <span className="o-kv__value">
                  {line.envelope.value === null ? (
                    <span className="o-muted">no data</span>
                  ) : line.envelope.unit === 'ratio' ? (
                    formatPercent(line.envelope.value)
                  ) : line.envelope.unit === 'cents' ? (
                    <MoneyText cents={line.envelope.value} showCents />
                  ) : (
                    String(line.envelope.value)
                  )}
                </span>
              </div>
            ))}
          </div>
        )}
        <p className="o-muted">
          Accrual. Sales tax is a pass through and is listed for completeness, not as revenue.
          Reserve movements, purchase order payments, owner draws and debt principal are not on
          this statement at all.
        </p>
      </Card>

      <TrendsSection window={window} rows={products.data?.items ?? []} loading={products.isLoading} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Trends                                                              */
/* ------------------------------------------------------------------ */

export function TrendsSection({
  window,
  rows,
  loading,
}: {
  window: WindowKind;
  rows: ProductPnlRow[];
  loading: boolean;
}) {
  const columns: readonly Column<ProductPnlRow>[] = [
    { id: 'sku', header: 'SKU', priority: 1, mono: true, render: (r) => r.sku ?? r.name },
    { id: 'units', header: 'Units', priority: 1, align: 'right', render: (r) => r.units },
    {
      id: 'revenue',
      header: 'Revenue',
      priority: 1,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.revenue_cents} showCents={false} />,
    },
    {
      id: 'avg',
      header: 'Average sell',
      priority: 3,
      align: 'right',
      money: true,
      render: (r) => (r.units === 0 ? '' : <MoneyText cents={Math.round(r.revenue_cents / r.units)} showCents />),
    },
    {
      id: 'cogs',
      header: 'COGS',
      priority: 3,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.cogs_cents} showCents={false} />,
    },
    {
      id: 'contribution',
      header: 'Contribution',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) => <MoneyText cents={r.contribution_cents} showCents={false} />,
    },
    {
      id: 'margin',
      header: 'Gross margin',
      priority: 2,
      align: 'right',
      money: true,
      render: (r) => (r.gross_margin_pct === null ? <span className="o-muted">n/a</span> : formatPercent(r.gross_margin_pct)),
    },
    {
      id: 'no_cost',
      header: 'Sold without cost',
      priority: 5,
      align: 'right',
      render: (r) => (r.lines_without_cost > 0 ? <Pill tone="crit">{r.lines_without_cost}</Pill> : ''),
    },
  ];

  const totalRevenue = rows.reduce((sum, r) => sum + r.revenue_cents, 0);
  const totalContribution = rows.reduce((sum, r) => sum + r.contribution_cents, 0);

  return (
    <Card title={`Trends · by SKU · ${window}`} basis="woo_paid">
      <div className="o-scorecard">
        <div className="o-kpi">
          <span className="o-kpi__label">Product revenue</span>
          <span className="o-kpi__value">{formatMoney(totalRevenue, { showCents: false })}</span>
          <span className="o-kpi__meta">line items only, not revenue net</span>
        </div>
        <div className="o-kpi">
          <span className="o-kpi__label">Contribution after fees</span>
          <span className="o-kpi__value">{formatMoney(totalContribution, { showCents: false })}</span>
          <span className="o-kpi__meta">{rows.length} SKUs with sales</span>
        </div>
        <div className="o-kpi">
          <span className="o-kpi__label">Sold without a cost</span>
          <span className="o-kpi__value">{rows.reduce((sum, r) => sum + r.lines_without_cost, 0)}</span>
          <span className="o-kpi__meta">lines with no WAC, margin renders n/a</span>
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(r) => String(r.product_id)}
        caption="Revenue, cost and margin by SKU"
        loading={loading}
        emptyState={<EmptyState message="No paid lines in this window." />}
        allowHorizontalScroll
      />
      <p className="o-muted">
        Product revenue is line item totals after coupons and never sits beside revenue net on a
        card, because it excludes shipping, fees and protection. A SKU that sold without a cost
        shows n/a rather than a hundred percent margin.
      </p>
    </Card>
  );
}
